import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { LoadingSpinner } from "widgets";
import { useURLSearchParam } from "widgets/hooks";

import styleLayout from "../core/styles/PageLayout.module.scss";

import ColumnSelectionButton from "../core/ColumnSelectionButton.js";
import DataTableContainer from "../core/grid/DataTableContainer.js";
import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import PageLayout from "../core/navigation-panel/PageLayout.js";
import { useEventNotificationGridUpdate } from "@/app-utils/hooks/useNotificationUpdate.ts";
import { type InputTableQuerySpec } from "@/graphql.ts";
import { fetchPlaybookCategoryById } from "@/redux/modules/playbook-admin.ts";
import { type ITableData, createUniqueKeyFromTableSpec, getTableDataSlice } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE, EVENT_TABLE_COLUMNS } from "@/settings/settings.js";

export const EVENTS_MENU_ITEMS: INavPanelCategory[] = [
  {
    label: "Playbook Events",
    tooltip: "Show details of past Playbook runs",
    // licenseModule: LICENSE_MODULE.ANALYST,
    navItems: [
      {
        label: "Overview",
        route: "",
        pageComponent: <ContentBrowserEventsTablePage />,
      },
    ],
  },
];

export default function EventsPage() {
  return <PageLayout navPanelCategories={EVENTS_MENU_ITEMS} navPanelCollapsed />;
}

const eventsTableSpec: InputTableQuerySpec = {
  resultType: ENTITY_TYPE.EVENT,
};

const eventsTableInitOptions = {
  sortExpression: `-${EVENT_TABLE_COLUMNS.CREATED}`,
};

function ContentBrowserEventsTablePage() {
  const tableData: ITableData = useSelector<IAppState, ITableData>(
    (state) => getTableDataSlice(state.tableData, eventsTableSpec),
    shallowEqual,
  );

  const categoryId = useURLSearchParam("filterByCategory");
  const dispatch = useDispatch<ReduxDispatch>();
  const [categoryName, setCategoryName] = useState<string>();

  useEffect(() => {
    if (categoryId) {
      dispatch(fetchPlaybookCategoryById(Number(categoryId))).then((category) => {
        setCategoryName(category.name);
      });
    }
  }, [categoryId]);

  // create count label
  const getCount = (): string => {
    if (tableData?.totalRowCount) {
      if (tableData.filteredRowCount === tableData.totalRowCount) {
        return String(tableData.totalRowCount);
      } else if (tableData.filteredRowCount < tableData.totalRowCount) {
        return `${tableData.filteredRowCount} of ${tableData.totalRowCount}`;
      }
    }
  };

  useEventNotificationGridUpdate(eventsTableSpec);

  return (
    <>
      <PageHeader
        title={"Playbook Events"}
        setDocumentTitle
        badge={getCount()}
        actionWidgets={[
          <ColumnSelectionButton
            portalContext={createUniqueKeyFromTableSpec(tableData?.tableQuerySpec)}
            key={"events-page-widget-1"}
          />,
        ]}
      />
      <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
        {categoryId && !categoryName ? (
          // if a filterByCategory query param exists but fetching the category name has not finished yet
          <LoadingSpinner inCenter />
        ) : (
          <DataTableContainer
            key={createUniqueKeyFromTableSpec(eventsTableSpec)}
            tableSpec={eventsTableSpec}
            hideCheckboxes={true}
            tableInitializationOptions={{
              ...eventsTableInitOptions,
              filterExpression: categoryName && `playbook.categories.name=in=('${categoryName}')`,
            }}
          />
        )}
      </div>
    </>
  );
}
